import classNames from 'classnames';
import React from 'react';
import { Icon } from '@material-ui/core';
import { Link, LinkProps } from 'react-router-dom';

export interface CardProps extends React.HTMLAttributes<HTMLElement> {
  className?: string;

  /**
   * シャドウなしかどうか
   */
  isNoShadow?: boolean;

  /**
   * ボーダーありかどうか
   */
  isBordered?: boolean;

  /**
   * float: left; を適用する
   */
  isFloatLeft?: boolean;

  /**
   * float: right; を適用する
   */
  isFloatRight?: boolean;
}

const Card: React.FC<CardProps> & {
  Header: typeof CardHeader;
  Content: typeof CardContent;
  Actions: typeof CardActions;
  Link: typeof CardLink;
} = ({ className, children, isNoShadow = false, isBordered = false, isFloatLeft = false, isFloatRight = false, ...props }) => {
  const classes = classNames(
    'uc-card',
    className,
    (isNoShadow || isBordered) && 'uc-card--no-shadow',
    isBordered && 'uc-card--border',
    isFloatLeft && 'uc-card--left',
    isFloatRight && 'uc-card--right'
  );

  return (
    <div className={classes} {...props}>
      {children}
    </div>
  );
};

export interface CardHeaderProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * ヘッダーの右側エリア
   */
  right?: React.ReactNode;

  /**
   * アイコン
   */
  icon?: string;

  /**
   * アイコンに適用させるクラス名
   */
  iconClassName?: string;

  /**
   * 商品内容表示の幅調整
   */
  isNegotiationList?: boolean;
}

const CardHeader: React.FC<CardHeaderProps> = ({
  className,
  right,
  icon,
  iconClassName,
  isNegotiationList = false,
  children,
  ...props
}) => {
  const classes = classNames('uc-card__header', className);

  return (
    <div className={classes} {...props}>
      {icon && <Icon className={classNames('uc-card__header-icon', iconClassName)}>{icon}</Icon>}
      <h4 className={classNames('uc-card__header-title', isNegotiationList && 'uc-card__content-left')}>{children}</h4>
      {right && <div className={classNames('uc-card__header-right', isNegotiationList && 'uc-card__content-right')}>{right}</div>}
    </div>
  );
};

export type CardContentProps = React.HTMLAttributes<HTMLElement>;

const CardContent: React.FC<CardContentProps> = ({ className, children, ...props }) => {
  const classes = classNames('uc-card__content', className);

  return (
    <div className={classes} {...props}>
      {children}
    </div>
  );
};

export interface CardActionsProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * ボタンの配置 `left`, `center`, `right`
   */
  align?: 'left' | 'center' | 'right';
}

const CardActions: React.FC<CardActionsProps> = ({ className, children, align = 'left', ...props }) => {
  const classes = classNames('uc-card__actions', `uc-card__actions--${align}`, className);
  return (
    <div className={classes} {...props}>
      {children}
    </div>
  );
};

export interface CardLinkProps extends LinkProps, React.RefAttributes<HTMLAnchorElement> {}

const CardLink: React.FC<CardLinkProps> = ({ className, to, children, ...props }) => {
  const classes = classNames('uc-card__link', className);
  return (
    <Link to={to} className={classes} {...props}>
      {children}
    </Link>
  );
};

Card.Header = CardHeader;
Card.Content = CardContent;
Card.Actions = CardActions;
Card.Link = CardLink;

export default Card;
